import { UPSI_HISTORY_VERBS, UPSI_STAGES, UPSI_STAGE_LABELS } from '../../../../utils/constants';
import { formatUtcDateTime } from '../../../../utils/helpers';

/** lb-dialog width. Kept off the stylesheet; the shell already takes a width prop. */
export const UPSI_DIALOG_WIDTH = 640;

export const UPSI_EMPTY = '\u2014';

export const UPSI_SUMMARY_LABELS = {
  CYCLE: 'Cycle',
  UPSI_STATUS: 'UPSI status',
  PUBLICATION_DATE: 'Publication date',
  PUBLICATION_TIME: 'Publication time',
  PUBLICATION_DATE_TIME: 'Publication date & time',
  FILING_REFERENCE: 'Filing Reference',
};

export const UPSI_FIELD_LABELS = {
  FILING_REFERENCE: 'Filing Reference',
  FILING_REFERENCE_PLACEHOLDER: 'Enter the filing reference',
  REMARKS_OPTIONAL: 'Remarks (Optional)',
  REMARKS: 'Remarks',
  REMARKS_PLACEHOLDER: 'Add remarks',
  HISTORY: 'History',
  PUBLISH: 'Request for publish',
  CANCEL: 'Cancel',
  REJECT: 'Reject',
  APPROVE: 'Approve & publish',
  CLOSE: 'Close',
  PUBLISH_AGAIN: 'Publish again',
  REVIEW: 'Review',
};

/** History actions that can show the request's filing reference. */
const UPSI_HISTORY_FILING_ACTIONS = ['requested', 'approved', 'rejected', 'withdrawn'];

export const UPSI_HISTORY_ACTOR_FALLBACK = 'User';

/** Role words used when the details payload has no display name. */
export const UPSI_NAME_FALLBACK = {
  REVIEWER: 'Controller',
  REQUESTER: 'the requester',
};

export function fillUpsiTemplate(template, values) {
  return String(template || '').replace(/\{(\w+)\}/g, (match, key) => {
    const value = values && values[key];
    return value == null || value === '' ? '' : String(value);
  });
}

export function upsiStageLabel(stage) {
  const numeric = stage == null || stage === '' ? UPSI_STAGES.UNPUBLISHED : Number(stage);
  if (Number.isNaN(numeric) || !Object.prototype.hasOwnProperty.call(UPSI_STAGE_LABELS, numeric)) {
    return UPSI_STAGE_LABELS[UPSI_STAGES.UNPUBLISHED];
  }
  return UPSI_STAGE_LABELS[numeric];
}

export function readServerError(errors, keys) {
  const source = errors && typeof errors === 'object' ? errors : {};
  const names = Array.isArray(keys) ? keys : [keys];
  for (let index = 0; index < names.length; index += 1) {
    const key = names[index];
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const value = source[key];
    if (Array.isArray(value)) return value.filter(Boolean).join(' ');
    if (value && typeof value === 'object') return value.message || '';
    if (value) return String(value);
  }
  return '';
}

export function displayName(name, fallback) {
  const text = name == null ? '' : String(name).trim();
  return text || fallback;
}

export function upsiStateOf(details) {
  return (details && details.upsi_state) || {};
}

export function formatPublicationDateTime(iso) {
  return formatUtcDateTime(iso, { part: 'datetime' });
}

/**
 * Scroll a UPSI dialog body to the field errors under a long activity history.
 * lb-dialog marks the card text as the scroll container.
 * @param {HTMLElement|null|undefined} bodyEl
 */
export function scrollUpsiDialogToBottom(bodyEl) {
  if (!bodyEl || typeof bodyEl.closest !== 'function') return;
  const scroller = bodyEl.closest('.v-card__text') || bodyEl;
  if (typeof scroller.scrollTo === 'function') {
    scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
    return;
  }
  scroller.scrollTop = scroller.scrollHeight;
}

function historyTime(entry) {
  const stamp = entry && entry.timestamp;
  return new Date(stamp).getTime();
}

function filingReferenceForHistoryEntry(entry, rows, fallback) {
  const own = entry && entry.meta && entry.meta.filing_reference;
  if (own != null && String(own).trim() !== '') return String(own).trim();
  const requestId = entry && entry.request_id;
  if (requestId != null) {
    const sibling = rows.find((row) => {
      if (!row || !row.meta || row.meta.filing_reference == null) return false;
      return String(row.request_id) === String(requestId);
    });
    if (sibling) return String(sibling.meta.filing_reference).trim();
  }
  const action = entry && entry.action;
  if (UPSI_HISTORY_FILING_ACTIONS.includes(action) && fallback) return String(fallback).trim();
  return '';
}

/**
 * Oldest-first activity rows for every UPSI dialog.
 * @param {Array} history
 * @param {string} [filingReference] current request reference, used when a decision row has none
 * @returns {Array<{ id: *, actor: string, title: string, metaLine: string, message: string }>}
 */
export function buildUpsiActivityEntries(history, filingReference) {
  const rows = Array.isArray(history) ? history.slice() : [];
  rows.sort((left, right) => {
    const leftTime = historyTime(left);
    const rightTime = historyTime(right);
    const safeLeft = Number.isNaN(leftTime) ? 0 : leftTime;
    const safeRight = Number.isNaN(rightTime) ? 0 : rightTime;
    return safeLeft - safeRight;
  });
  return rows.map((entry) => {
    const action = entry && entry.action ? String(entry.action) : '';
    const verb = UPSI_HISTORY_VERBS[action];
    const actor = displayName(entry && entry.actor_name, UPSI_HISTORY_ACTOR_FALLBACK);
    const date = formatUtcDateTime(entry && entry.timestamp, { part: 'date' });
    const time = formatUtcDateTime(entry && entry.timestamp, { part: 'time' });
    const filing = filingReferenceForHistoryEntry(entry, rows, filingReference);
    const metaParts = [];
    if (date !== UPSI_EMPTY) metaParts.push(date);
    if (time !== UPSI_EMPTY) metaParts.push(time);
    if (filing) metaParts.push(filing);
    const message = entry && typeof entry.message === 'string' ? entry.message.trim() : '';
    return {
      id: entry && entry._id,
      actor,
      title: verb ? `${actor} ${verb}` : actor,
      metaLine: metaParts.join(' · '),
      message,
    };
  });
}
